import React, { useState } from "react";
import { CheckCircle2, Loader2, ShieldCheck, CreditCard } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SignaturePad } from "@/components/SignaturePad";
import { PRICING, FEE_NOTE, startCheckout } from "@/lib/payments";
import { SharePrompt } from "@/components/SharePrompt";

const inputCls =
  "w-full rounded-none border border-white/15 bg-zinc-900 px-4 py-3 font-body text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-neon-green focus:ring-1 focus:ring-neon-green";
const labelCls = "block font-body text-xs font-bold uppercase tracking-[0.2em] text-neon-green/80 mb-2";

export function WaiverForm({ regId, prefill = {}, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    participant_name: prefill.player_name || "",
    date_of_birth: "",
    parent_guardian_name: prefill.guardian_name || "",
    phone: prefill.phone || "",
    emergency_contact: "",
    emergency_contact_phone: "",
    photo_release: null,
    printed_name: prefill.guardian_name || "",
    signed_date: today,
    is_minor: true,
  });
  const [signature, setSignature] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.photo_release === null) return setError("Please choose Yes or No for the Photo & Media Release.");
    if (!agreed) return setError("You must acknowledge and agree to the waiver terms.");
    if (!signature) return setError("Please provide your signature in the box above.");
    setLoading(true);
    try {
      await api.patch(`/registrations/${regId}/waiver`, {
        ...form,
        photo_release: !!form.photo_release,
        signature,
        agreed: true,
      });
      setDone(true);
      onDone && onDone();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit waiver. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    const pricing = prefill.age_group ? PRICING[prefill.age_group] : null;
    return (
      <div data-testid="waiver-success" className="border border-neon-green/40 bg-zinc-900 p-10 text-center neon-glow">
        <CheckCircle2 size={48} strokeWidth={2.5} className="mx-auto text-neon-green" />
        <h3 className="mt-5 font-heading text-3xl uppercase text-white">All Set!</h3>
        <p className="mt-3 font-body text-sm text-white/60">
          Registration complete and your liability waiver is signed. {pricing ? "Complete your payment below to secure your player's spot." : "Our team will be in touch with next steps."}
        </p>
        {pricing && (
          <div>
            <button
              onClick={() => startCheckout(pricing.lookup_key, { player_name: prefill.player_name || "", email: prefill.email || "", registration_id: regId || "" })}
              data-testid="waiver-pay-btn"
              className="mt-6 inline-flex items-center gap-2 bg-neon-green px-8 py-4 font-heading text-sm uppercase tracking-widest text-black"
            >
              Pay Now — {pricing.total}
              <CreditCard size={18} strokeWidth={2.5} />
            </button>
            <p className="mt-2 font-body text-xs text-white/40">{pricing.price} training + $5 online payment fee</p>
          </div>
        )}
        <SharePrompt />
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="waiver-form" className="space-y-8">
      <div className="flex items-center gap-3 border-b border-white/10 pb-5">
        <ShieldCheck size={26} strokeWidth={2.5} className="text-neon-green" />
        <div>
          <h3 className="font-heading text-2xl uppercase leading-none text-white">Liability Waiver & Release</h3>
          <p className="font-body text-xs uppercase tracking-widest text-neon-green">ARD MidSouth Performance & Northpoint Christian School</p>
        </div>
      </div>

      {error && (
        <div data-testid="waiver-error" className="border border-red-500/50 bg-red-500/10 px-4 py-3 font-body text-sm text-red-300">{error}</div>
      )}

      {/* Participant details */}
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label className={labelCls}>Participant Name *</label>
          <input required data-testid="waiver-participant" className={inputCls} value={form.participant_name} onChange={set("participant_name")} />
        </div>
        <div>
          <label className={labelCls}>Date of Birth *</label>
          <input required type="date" data-testid="waiver-dob" className={inputCls} value={form.date_of_birth} onChange={set("date_of_birth")} />
        </div>
        <div>
          <label className={labelCls}>Parent / Guardian Name (if under 18)</label>
          <input data-testid="waiver-guardian" className={inputCls} value={form.parent_guardian_name} onChange={set("parent_guardian_name")} />
        </div>
        <div>
          <label className={labelCls}>Phone Number *</label>
          <input required data-testid="waiver-phone" className={inputCls} value={form.phone} onChange={set("phone")} />
        </div>
        <div>
          <label className={labelCls}>Emergency Contact *</label>
          <input required data-testid="waiver-emergency" className={inputCls} value={form.emergency_contact} onChange={set("emergency_contact")} />
        </div>
        <div>
          <label className={labelCls}>Emergency Contact Phone *</label>
          <input required data-testid="waiver-emergency-phone" className={inputCls} value={form.emergency_contact_phone} onChange={set("emergency_contact_phone")} />
        </div>
      </div>

      {/* Legal text */}
      <div className="max-h-72 space-y-4 overflow-y-auto border border-white/10 bg-obsidian p-5 font-body text-sm leading-relaxed text-white/70" data-testid="waiver-text">
        <div>
          <h4 className="font-heading text-base uppercase text-white">Assumption of Risk</h4>
          <p className="mt-1">I acknowledge that participation in athletic training, strength and conditioning, speed and agility training, fitness instruction, sports performance activities, and related programs conducted by ARD MidSouth Performance ("ARD") at Northpoint Christian School ("NCS") involves inherent risks. I voluntarily choose to participate in these activities and knowingly assume all risks associated with my participation.</p>
        </div>
        <div>
          <h4 className="font-heading text-base uppercase text-white">Medical Fitness</h4>
          <p className="mt-1">I certify that I (or my child) am physically capable of participating in these activities and have no medical condition that would make participation unsafe. I understand it is my responsibility to consult a physician before participating if I have any concerns regarding my health.</p>
        </div>
        <div>
          <h4 className="font-heading text-base uppercase text-white">Release of Liability</h4>
          <p className="mt-1">In consideration of being allowed to participate in programs conducted by ARD MidSouth Performance at Northpoint Christian School, I, on behalf of myself and my children, hereby release, waive, discharge, and hold harmless: ARD MidSouth Performance; Northpoint Christian School; and their owners, directors, officers, employees, coaches, trainers, volunteers, agents, and representatives from any and all claims, liabilities, demands, damages, losses, costs, expenses, or causes of action arising out of or relating to any injury, illness, disability, property damage, or other loss sustained while participating in training or activities at Northpoint Christian School, ARD MidSouth Performance facilities, or any location where ARD-sponsored activities are conducted, except where prohibited by applicable law.</p>
        </div>
        <div>
          <h4 className="font-heading text-base uppercase text-white">Acknowledgment</h4>
          <p className="mt-1">I have carefully read this Liability Waiver and Release Agreement. I understand its contents and acknowledge that I am signing it voluntarily. I understand that by signing this document, I am giving up certain legal rights.</p>
        </div>
      </div>

      {/* Photo release */}
      <div>
        <label className={labelCls}>Photo & Media Release (Optional)</label>
        <p className="mb-3 font-body text-sm text-white/60">I grant permission for ARD MidSouth Performance to use photographs or videos of me (or my child) taken during training sessions for promotional, educational, or marketing purposes without compensation.</p>
        <div className="flex gap-3">
          {[["Yes", true], ["No", false]].map(([label, val]) => (
            <button
              key={label}
              type="button"
              data-testid={`photo-release-${label.toLowerCase()}`}
              onClick={() => setForm((f) => ({ ...f, photo_release: val }))}
              className={`flex-1 border py-3 font-heading text-lg uppercase tracking-wide transition-colors ${
                form.photo_release === val ? "border-neon-green bg-neon-green text-black" : "border-white/15 bg-zinc-900 text-white/70 hover:border-neon-green/60"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Consent + signature */}
      <div className="border border-white/10 bg-obsidian p-5">
        <p className="font-body text-xs uppercase tracking-widest text-neon-green">Parent / Guardian Consent (Required if participant is under 18)</p>
        <p className="mt-2 font-body text-sm text-white/70">I am the parent or legal guardian of the participant listed above. I have read and understand this Liability Waiver and Release Agreement, and I consent to my child's participation. I agree to its terms on behalf of myself and my child.</p>

        <label className="mt-4 flex cursor-pointer items-start gap-3">
          <input type="checkbox" data-testid="waiver-agree" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 h-5 w-5 accent-[#39FF14]" />
          <span className="font-body text-sm text-white/80">I have read, understand, and agree to the Liability Waiver and Release of Claims above.</span>
        </label>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className={labelCls}>Signature *</label>
          <SignaturePad onChange={setSignature} />
        </div>
        <div>
          <label className={labelCls}>Printed Name *</label>
          <input required data-testid="waiver-printed" className={inputCls} value={form.printed_name} onChange={set("printed_name")} />
        </div>
        <div>
          <label className={labelCls}>Date *</label>
          <input required type="date" data-testid="waiver-date" className={inputCls} value={form.signed_date} onChange={set("signed_date")} />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="submit-waiver-btn"
        className="flex w-full items-center justify-center bg-neon-green py-4 font-heading text-lg uppercase tracking-widest text-black transition-opacity disabled:opacity-60"
      >
        {loading ? <Loader2 className="animate-spin" size={22} /> : "Agree & Sign Waiver"}
      </button>
    </form>
  );
}
